import { Box, Divider } from '@material-ui/core';
import { calculPourcentage, cumulData } from 'core/utils/utils';
import React from 'react';

import { BarChart, Bar, XAxis, YAxis, Label, Line, Legend, ResponsiveContainer, Tooltip } from 'recharts';

type Axis = {
    x: any;
    y: any;
    width: any;
    height: any;
};

type Chart = {
    data: any;
    value: any;
    viewBox: Axis;
};

type LegendLabels = {
    pv: string;
    uv: string;
};

type Props = {
    data: any;
    width?: number;
    height?: number;
    color1?: string;
    color2?: string;
    labelVertical?: string;
    labelHorizontal?: string;
    legend?: boolean;
    legendLabels?: LegendLabels;
};

const SGSPyramidChart: React.FC<Props> = ({
    data,
    width = 525,
    height = 300,
    color1 = '#2574A9', // Bleu
    color2 = '#EB4C37', // rouge
    labelVertical,
    labelHorizontal,
    legend,
    legendLabels,
}) => {
    // Get max value to set domain (plage de chiffres axes X)
    const values = [];
    data.forEach((d) => {
        values.push(Math.abs(d.uv));
        values.push(Math.abs(d.pv));
    });

    // Arrondir à la dizaine supérieur pour équilibrer le graph
    const maxValue = data.length ? 10 * Math.floor(Math.max(...values) / 10) + 10 : 0;

    // Calcutate total
    const subTotal1 = data.length ? data.map((e) => Math.abs(e.uv) + Math.abs(e.pv)).reduce(cumulData, 0) : 0;
    const subTotal2 = data.length ? data.map((e) => Math.abs(e.uv) + Math.abs(e.pv)).reduce(cumulData, 0) : 0;
    const total = subTotal1 + subTotal2;

    return (
        <>
            <ResponsiveContainer width={'100%'} height={300} min-width={300}>
                <BarChart width={width} height={height} data={data} layout="vertical" stackOffset="sign">
                    <XAxis type="number" tickFormatter={formatYAxis} domain={[-Math.abs(maxValue), Math.abs(maxValue)]}>
                        <Label
                            value={labelHorizontal ? labelHorizontal : ''}
                            //position="insideBottomRight"
                            //offset={100}
                            content={(props: Chart) => {
                                return (
                                    <text
                                        style={{ fontSize: '12px' }}
                                        x={props.viewBox.x + props.viewBox.width / 2 + 30}
                                        y={props.viewBox.y + props.viewBox.height + 3}
                                        text-anchor="end"
                                        fill="#bfbfbf"
                                    >
                                        {props.value}
                                    </text>
                                );
                            }}
                        />
                    </XAxis>

                    <YAxis
                        dataKey="name"
                        type="category"
                        width={100}
                        style={{
                            fontSize: '0.8rem',
                            fontFamily: 'Arial',
                            whiteSpace: 'nowrap',
                            width: '200px !important',
                        }}
                    >
                        <Label angle={270} position="left" style={{ textAnchor: 'middle' }}>
                            {labelVertical && { labelVertical }}
                        </Label>
                    </YAxis>
                    <Bar dataKey="pv" stackId="a" fill={color1} isAnimationActive={false} />
                    <Bar dataKey="uv" stackId="a" fill={color2} isAnimationActive={false} />

                    <Tooltip
                        shared={false}
                        content={
                            <CustomTooltip
                                active={undefined}
                                payload={undefined}
                                total={total}
                                metric={''}
                                legendLabels={legendLabels}
                            />
                        }
                    />

                    {legend && (
                        <>
                            <Legend verticalAlign="bottom" height={36} align="center" />
                            <Line name={legendLabels.pv} type="monotone" dataKey="pv" stroke={color1} />
                            <Line name={legendLabels.uv} type="monotone" dataKey="uv" stroke={color2} />
                        </>
                    )}
                </BarChart>
            </ResponsiveContainer>
        </>
    );
};

export default SGSPyramidChart;

function formatYAxis(value: any) {
    return Math.abs(value).toString();
}

const CustomTooltip = ({ active, payload, total, metric, legendLabels }) => {
    if (active) {
        const pourcentage = calculPourcentage(total, payload[0].value);
        const label = payload[0].name === 'pv' ? legendLabels.pv : legendLabels.uv;
        return (
            <Box
                className="custom-tooltip"
                style={{
                    backgroundColor: '#626466',
                    color: 'white',
                    padding: '5px',
                    border: '1px solid #efefef !important',
                    borderRadius: '5px',
                }}
            >
                {metric === 'pourcentage' ? (
                    <>
                        <Box className="text-center">
                            {`${label}`} ~ {pourcentage}%
                        </Box>
                    </>
                ) : (
                    <>
                        <Box className="text-center">{`${label}`}</Box>
                        <Divider />
                        <Box className="text-center">
                            {`${Math.abs(payload[0].value)}`} ~ {Math.abs(parseFloat(pourcentage))}%
                        </Box>
                    </>
                )}
            </Box>
        );
    }
    return null;
};
